/**
 * Completeness confirmation banner — "Is this all the books and records?".
 *
 * Purpose: make the agent's working assumption explicit without dominating the
 * panel. The verdict is always reached on the books and records uploaded to
 * Morgan; this slim banner lets the practitioner confirm whether that upload is
 * the complete set and, when it isn't, capture why (via a pop-out) and surface
 * the recommended next step (ASIC assistance / s533 report). It never locks the
 * verdict — the agent keeps reassessing as new documents arrive.
 */

import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { toast } from "sonner";
import { Button } from "@/ui/primitives/button";
import { Textarea } from "@/ui/primitives/textarea";
import { Badge } from "@/ui/primitives/badge";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/ui/composed/overlays";
import { cn } from "@/utils/misc";
import {
  CheckCircle2,
  HelpCircle,
  Lightbulb,
  TriangleAlert,
} from "lucide-react";
import { COMPLETENESS_REASON_META } from "./meta";
import type { Assessment, CompletenessReason } from "./types";

const REASON_ORDER: CompletenessReason[] = [
  "withheld",
  "no_access",
  "do_not_exist",
  "not_yet_received",
];

export function CompletenessCard({
  jobId,
  assessment,
  isViewer,
}: {
  jobId: Id<"jobs">;
  assessment: Assessment;
  isViewer?: boolean;
}) {
  const setCompleteness = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.booksAndRecords.publicMutations
        .setCompleteness,
    ),
  });

  const current = assessment.completeness;
  const canEdit = !isViewer;

  // Pop-out ("No, something is missing" / "Change reason" / "Mark incomplete").
  const [picking, setPicking] = useState(false);
  const [reason, setReason] = useState<CompletenessReason | null>(
    current?.reason ?? null,
  );
  const [note, setNote] = useState(current?.note ?? "");

  const confirmComplete = async () => {
    try {
      await setCompleteness.mutateAsync({ jobId, isComplete: true });
      setPicking(false);
      toast.success("Confirmed as the complete set of books and records");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save");
    }
  };

  const confirmIncomplete = async () => {
    if (!reason) {
      toast.error("Select a reason");
      return;
    }
    try {
      await setCompleteness.mutateAsync({
        jobId,
        isComplete: false,
        reason,
        note: note.trim() || undefined,
      });
      setPicking(false);
      toast.success("Recorded that the records are incomplete");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save");
    }
  };

  // Seed the pop-out with the current selection whenever it opens.
  const onOpenChange = (open: boolean) => {
    if (open) {
      setReason(current?.reason ?? null);
      setNote(current?.note ?? "");
    }
    setPicking(open);
  };

  const isIncomplete = current && !current.isComplete;
  const triggerLabel = !current
    ? "No, something is missing"
    : current.isComplete
      ? "Mark incomplete"
      : "Change reason";

  return (
    <div className="rounded-md border bg-muted/30 px-4 py-2.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <HelpCircle className="size-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium">
              Is this all the books and records?
            </p>
            {current && (
              <CompletenessStatusBadge isComplete={current.isComplete} />
            )}
          </div>
          {isIncomplete && current.reason ? (
            <div className="mt-0.5 space-y-1">
              <p className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">
                  Recommended next step.
                </span>{" "}
                {COMPLETENESS_REASON_META[current.reason].recommendation}
              </p>
              {current.note && (
                <p className="text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">Note:</span>{" "}
                  {current.note}
                </p>
              )}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              Verdict is based on data in Morgan. Select to confirm if data set
              is complete or mark missing records for action.
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          {!current && canEdit && (
            <Button
              size="sm"
              onClick={confirmComplete}
              disabled={setCompleteness.isPending}
            >
              Yes, this is all
            </Button>
          )}

          {current?.isComplete === false && canEdit && (
            <Button
              size="sm"
              variant="outline"
              onClick={confirmComplete}
              disabled={setCompleteness.isPending}
            >
              Mark complete
            </Button>
          )}

          {canEdit ? (
            <Popover open={picking} onOpenChange={onOpenChange}>
              <PopoverTrigger asChild>
                <Button size="sm" variant="outline">
                  {triggerLabel}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 space-y-3">
                <ReasonPicker
                  reason={reason}
                  note={note}
                  onReason={setReason}
                  onNote={setNote}
                  onConfirm={confirmIncomplete}
                  saving={setCompleteness.isPending}
                />
              </PopoverContent>
            </Popover>
          ) : (
            !current && (
              <span className="text-xs text-muted-foreground">
                Not yet confirmed.
              </span>
            )
          )}
        </div>
      </div>
    </div>
  );
}

function CompletenessStatusBadge({ isComplete }: { isComplete: boolean }) {
  return isComplete ? (
    <Badge color="emerald" variant="soft" size="sm" className="gap-1">
      <CheckCircle2 className="size-3" /> Marked complete
    </Badge>
  ) : (
    <Badge color="amber" variant="soft" size="sm" className="gap-1">
      <TriangleAlert className="size-3" /> Marked incomplete
    </Badge>
  );
}

function ReasonPicker({
  reason,
  note,
  onReason,
  onNote,
  onConfirm,
  saving,
}: {
  reason: CompletenessReason | null;
  note: string;
  onReason: (r: CompletenessReason) => void;
  onNote: (n: string) => void;
  onConfirm: () => void;
  saving: boolean;
}) {
  return (
    <div className="space-y-3">
      <p className="text-xs font-medium text-muted-foreground">
        Why are the records not complete?
      </p>
      <div className="space-y-1.5">
        {REASON_ORDER.map((key) => {
          const meta = COMPLETENESS_REASON_META[key];
          const Icon = meta.icon;
          const active = reason === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onReason(key)}
              className={cn(
                "flex w-full items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition",
                active
                  ? "border-primary bg-primary/5 text-primary"
                  : "hover:bg-muted/50",
              )}
            >
              <Icon className="size-4 shrink-0" />
              {meta.label}
            </button>
          );
        })}
      </div>

      {reason && <RecommendationCallout reason={reason} />}

      <Textarea
        className="min-h-[44px] text-sm"
        placeholder="Add any detail (optional)"
        value={note}
        onChange={(e) => onNote(e.target.value)}
      />

      <Button
        size="sm"
        className="w-full"
        onClick={onConfirm}
        disabled={saving || !reason}
      >
        Save
      </Button>
    </div>
  );
}

function RecommendationCallout({ reason }: { reason: CompletenessReason }) {
  const meta = COMPLETENESS_REASON_META[reason];
  const warning = meta.tone === "warning";
  return (
    <div
      className={cn(
        "flex items-start gap-2 rounded-md border p-2.5 text-xs leading-relaxed",
        warning
          ? "border-amber-200 bg-amber-50 text-amber-900"
          : "border-border bg-muted/40 text-foreground",
      )}
    >
      <Lightbulb className="mt-0.5 size-3.5 shrink-0" />
      <div>
        <span className="font-semibold">Recommended next step. </span>
        {meta.recommendation}
      </div>
    </div>
  );
}
